import { Injectable } from "@angular/core";
import gql from "graphql-tag";
import {
  AuthenticationService,
  clone,
  ConnextData,
  CreatePlantDeviceInput,
  CreatePlantEmsInput,
  CreatePlantUserInput,
  CreatePlantUserPrefInput,
  Device,
  GetConnextDataQuery,
  getSerialAndAttributeName,
  GraphqlApiService,
  isNotNullOrUndefined,
  MapService,
  MonitorPrefEnumType,
  PlantConfigInput,
  PlantDeviceEnumType,
  PlantEmsEnumType,
  PlantGeoInput,
  PlantMonitoringEnumType,
  ProductModelService,
  removeAllMaskIdFromDeviceList,
  System,
  UserPermissions,
} from "ngx-core-services";
import { first } from "rxjs/operators";

import { CreateAwsService } from "../create-aws/create-aws.service";
import { convertProductTypeNameToPlantDeviceType } from "../create-aws/create-aws.utils";
import { connextDataQueryForPorting } from "../block-state/query-constructor.query";
import { Portability } from "./base-systems-porting/base-systems-porting.models";
import { SystemDefinition } from "../../store";

@Injectable({
  providedIn: 'root'
})
export class PortingService {
  private _portingSystemUidPopup?: string;
  get portingSystemUidPopup() {
    return this._portingSystemUidPopup;
  }

  set portingSystemUidPopup(value: string | undefined) {
    this._portingSystemUidPopup = value;
  }

  constructor(
    private readonly auth: AuthenticationService,
    private readonly createAwsService: CreateAwsService,
    private readonly getConnextDataQuery: GetConnextDataQuery,
    private readonly graphqlApiService: GraphqlApiService,
    private readonly mapService: MapService,
  ) { }

  readonly graphqlClient$ = this.graphqlApiService.client$

  getDevicesIsAlreadyInPlants = async (deviceIds: string[]) => {
    if (deviceIds.length === 0) {
      return {};
    }

    const client = await this.graphqlClient$.pipe(
      isNotNullOrUndefined(),
      first()
    ).toPromise();

    let combinedResults: {[key: string]: Partial<{ plantId: string; }>} = {};

    const deviceIdsCopy = [...deviceIds];

    while (deviceIdsCopy.length > 0) {
      const CHUNK_SIZE = 20;
      const chunk = deviceIdsCopy.splice(0, CHUNK_SIZE);

      const query = gql`${this.buildDevicesIsAlreadyInPlantsQuery(chunk)}`;
      const result = clone(
        await client.query<{
          [key: string]: Partial<{ plantId: string }>;
        }>({ query, fetchPolicy: 'network-only' })
      );
      const processedResult = removeAllMaskIdFromDeviceList(result.data);
      combinedResults = { ...combinedResults, ...processedResult };
    }

    return combinedResults;
  };


  buildDevicesIsAlreadyInPlantsQuery = (deviceIds: string[]): string => {
    const deviceQueries = deviceIds.map((id) => {
      const product = new ProductModelService(id);
      const { serial, attributeName } = getSerialAndAttributeName(id);
      if (product.type?.name === 'connext') {
        return `
          ZZ${attributeName}Z: getCertifiedPlantIdByEmsId(emsId: "${serial.toLowerCase()}") {
            plantId
          }
        `;
      }
      return `
        ${attributeName}: getCertifiedPlantIdByDeviceId(deviceId: "${serial}") {
          plantId
        }
      `;
    });
    return `query { ${deviceQueries.join('')} }`;
  };

  portPlantToAws = async (system: System, connextData?: Partial<ConnextData>) => {
    const devices: CreatePlantDeviceInput[] = system.devices.map((device: Device) => ({
      installationTime: new Date().getTime(),
      type: convertProductTypeNameToPlantDeviceType(new ProductModelService(device.id).type?.name!),
      sn: new ProductModelService(device.id).type?.name  === 'inverter' ? device.id.toUpperCase() : device.id,
    }));

    let lng;
    let lat;
    // if connextSystem search for the coordinates in the connextData if not exist fallback to firebase coordinates
    if( system.configuration.connextId && connextData?.config?.longitude && connextData?.config?.latitude ) {
      lng = connextData?.config?.longitude;
      lat = connextData?.config?.latitude;
    } else {
      const coordinates = system.configuration.activeZone?.coordinates ?? '41.884326767819786,12.480541269505478';
      const coordinatesActZone = coordinates.split(',');
      if ( coordinatesActZone && coordinatesActZone.length > 1) {
        let longActZonePrecision = coordinatesActZone[1].trim().length - 1;
        let latActZonePrecision = coordinatesActZone[0].trim().length - 1;
        longActZonePrecision = longActZonePrecision > 0 ? longActZonePrecision : 1;
        latActZonePrecision = latActZonePrecision > 0 ? latActZonePrecision : 1;
        lng = +(Number(coordinatesActZone[1]).toPrecision(longActZonePrecision));
        lat = +(Number(coordinatesActZone[0]).toPrecision(latActZonePrecision));
      }
    }

    if( !lng || !lat ) {
      return false;
    }

    const latLng = await this.mapService.getLatLng({ lat, lng });
    const timezone = await this.mapService.getTz(latLng);

    const geo: PlantGeoInput = {
      latitude: latLng.lat()!,
      longitude: latLng.lng()!,
      IANA: timezone?.zoneName!,
    };

    const currentUser = await this.auth.user$.pipe(first()).toPromise();

    if( !currentUser || !currentUser.email ) {
      return false;
    }

    // the user want to port the system to AWS is the owner of the system and the only user of the system
    const users: CreatePlantUserInput[] = [{
      email: currentUser.email,
      userLevel: UserPermissions.Owner,
    }];

    if( system.source === 'sunflower' && system?.users && system.users.length > 0 ) {
      system.users.forEach((user) => {
        if( user.email !== currentUser.email ) {
          users.push({
            email: user.email,
            userLevel: UserPermissions.viewer,
          });
        }
      });
    }

    const userPref: CreatePlantUserPrefInput = {
      monitorPref: system.configuration.connextId ? MonitorPrefEnumType.CONNEXT : MonitorPrefEnumType.SOLARMAN,
      plantName: `${system.name} [Advanced]`,
    };

    const hasDeviceImportExport = devices.some((device) => device.type === PlantDeviceEnumType.COMBOX || device.type === PlantDeviceEnumType.METER);
    let config :PlantConfigInput = {};
    let ems: CreatePlantEmsInput | undefined;
    if( system.configuration.connextId) {
      ems = {
        installationTime: new Date().getTime(),
        type: PlantEmsEnumType.CONNEXT,
        sn: system.configuration.connextId,
      };
    }

    if( hasDeviceImportExport ) {
      config = {
        meterInversion: system.configuration.invertMeterSensor,
        monitoringType: system.configuration.connextId ? PlantMonitoringEnumType.CONNEXT : PlantMonitoringEnumType.SOLARMAN,
      };
    } else {
      config = {
        monitoringType: system.configuration.connextId ? PlantMonitoringEnumType.CONNEXT : PlantMonitoringEnumType.SOLARMAN,
      };
    }
    const result = await this.createAwsService.createAwsPlant(
      devices,
      geo,
      users,
      userPref,
      config,
      ems,
    );

    if( result?.statusCode !== 200 ) {
      return false;
    }
    return true;
  }


  async determinePortability(system?: SystemDefinition): Promise<Portability> {
    if (!system) {
      return Portability.NOT_PORTABLE;
    }

    if (system.configuration.connextId && system.metadata?.userLevel !== UserPermissions.Owner) {
      return Portability.NOT_PORTABLE;
    }

    const deviceIds = system.devices.map((d) => {
      const product = new ProductModelService(d.id);
      if (product.type?.name === 'inverter' || product.type?.name === 'enerclic') {
        return product.id.toUpperCase();
      }
      return d.id;
    });
    const remoteDevicesMap = await this.getDevicesIsAlreadyInPlants(deviceIds);

    const devicesFoundRemotely = deviceIds
      .filter(id => remoteDevicesMap[id]?.plantId)
      .map(device => ({
        device,
        plantIds: [remoteDevicesMap[device]?.plantId],
      }));

    return devicesFoundRemotely.length > 0
      ? Portability.NOT_PORTABLE
      : Portability.PORTABLE;
  }

  async getConnextData(uid: string) {
    return await this.getConnextDataQuery.execute(connextDataQueryForPorting, { id: uid }, true);
  }
}
